/**
 * Landing screen. SPEC.md §6.1, §6.2, §6.3, §34.5, §4.6.
 *
 * Two ways in and nothing else: create a table, or type the code someone just
 * read out across it. Creating asks for a name and an avatar — no account, no
 * password, no email. §6.1, §6.3
 *
 * The code field is deliberately forgiving. People paste the whole URL out of a
 * message, they type the dash they can see on the host's screen, and they type in
 * whatever case their keyboard happens to be in. All three normalise to the same
 * code through the shared join-code helpers. §6.2
 *
 * One more thing is chosen here and nowhere else: whether the night is played in
 * drinking mode. It is fixed at creation because it decides what a hand is worth,
 * and a table that switched halfway through would have two kinds of hand in one
 * set of totals. §4.6
 */

import { useState } from 'react';
import {
  DEFAULT_POINTS_PER_DRINK,
  MAX_POINTS_PER_DRINK,
  formatJoinCode,
  joinCodeSchema,
  messageForErrorCode,
  normalizeJoinCode,
  pointsPerDrinkSchema,
  type PlayerIdentityInput,
} from '@card-counter/shared';
import cinnaLogo from '@/assets/cinna.webp';
import cinnaDrinkLogo from '@/assets/cinna_drink.webp';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { Sheet } from '@/components/ui/Sheet';
import { LanguagePicker } from '@/features/tutorial/LanguagePicker';
import { createSession, errorMessageOf } from '@/lib/api';
import { writePlayerToken } from '@/lib/storage';
import { navigate, paths } from '@/router';
import { JoinForm } from './JoinForm';

/**
 * The wording for an unusable code, taken from the schema instead of repeated
 * here so the two can never disagree. The probe value passes the schema's length
 * checks — whose messages are developer-facing — and can only fail the refine,
 * which is the part written for a player. §35, §38
 */
const codeProbe = joinCodeSchema.safeParse('----');
const INVALID_CODE_MESSAGE = codeProbe.success
  ? messageForErrorCode('VALIDATION_FAILED')
  : (codeProbe.error.issues[0]?.message ?? messageForErrorCode('VALIDATION_FAILED'));

/** The dead-end screens link here with ?create so this opens on the form. §34.5 */
function initialMode(): 'choose' | 'create' {
  return new URLSearchParams(window.location.search).has('create') ? 'create' : 'choose';
}

/**
 * Reduces whatever is in the field to something worth validating: a pasted
 * `https://…/j/CAT284` to its last segment, a dictated "cat 284" to "CAT284". §6.2
 */
function readCode(input: string): string {
  const last = input.trim().split(/[/?#]/).filter(Boolean).pop() ?? '';
  return normalizeJoinCode(last);
}

/** What the field shows while typing: uppercase, with a pasted URL collapsed. §6.2 */
function displayCode(input: string): string {
  if (/[/:]/.test(input)) {
    const code = readCode(input);
    return code.length > 0 ? formatJoinCode(code) : '';
  }
  return input.toUpperCase();
}

/**
 * What the header is showing, once a player has picked a character. §6.3
 *
 * `nonce` and not just the key, because the artwork is remounted on it: the
 * stretch is a CSS animation, and re-rendering an element whose `animation-name`
 * has not changed does not start it again — the browser has no reason to think
 * anything happened. So the second pick, and every pick after it, would swap the
 * artwork with no motion at all: not a visible backlog, which someone would
 * report, but a silent nothing. A fresh element always starts fresh. The key
 * alone would not do it either: re-picking the character already shown is still
 * a tap, and a tap that produced nothing reads as a tap that was missed.
 */
interface AvatarPreview {
  avatarKey: string;
  nonce: number;
}

export function LandingPage() {
  const [mode, setMode] = useState<'choose' | 'create'>(initialMode);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [preview, setPreview] = useState<AvatarPreview | null>(null);

  /**
   * Drinking mode, and the two pieces of state it needs before a table exists.
   *
   * `drinkingNonce` is the same trick `AvatarPreview` uses and for the same
   * reason: the header artwork is remounted on it so the stretch runs on every
   * toggle, including the one that goes back to the ordinary logo. A toggle that
   * produced no motion would read as a toggle that was missed. §6.3
   */
  const [drinkingMode, setDrinkingMode] = useState(false);
  const [drinkingNonce, setDrinkingNonce] = useState(0);
  /** Asked once, on Create Game, because it prices the whole night. §4.6 */
  const [askingPrice, setAskingPrice] = useState(false);
  const [priceDraft, setPriceDraft] = useState(String(DEFAULT_POINTS_PER_DRINK));
  const [priceError, setPriceError] = useState<string | null>(null);
  const [pendingIdentity, setPendingIdentity] = useState<PlayerIdentityInput | null>(null);

  /**
   * The tutorial's language, asked once and then never again — the answer lives
   * in the URL the picker navigates to rather than in storage, so a link to
   * `/tutorial/th` is a link to the Thai tutorial for whoever opens it. §47
   */
  const [pickingLanguage, setPickingLanguage] = useState(false);

  function submitCode(): void {
    const parsed = joinCodeSchema.safeParse(readCode(code));
    if (!parsed.success) {
      setCodeError(INVALID_CODE_MESSAGE);
      return;
    }
    // Whether that table exists, is locked or is full is the join screen's
    // problem — it has the copy and the actions for all three. §34.4, §34.5
    navigate(paths.join(parsed.data));
  }

  async function create(
    identity: PlayerIdentityInput,
    pointsPerDrink: number | null,
  ): Promise<void> {
    setCreating(true);
    setCreateError(null);
    try {
      const credentials = await createSession({
        ...identity,
        ...(pointsPerDrink === null ? {} : { drinkingMode: true, pointsPerDrink }),
      });
      // Stored before navigating: the table screen proves identity from this token
      // and would otherwise send us straight back here. §6.4
      writePlayerToken(credentials.sessionId, credentials.playerToken);
      navigate(paths.game(credentials.sessionId));
    } catch (err) {
      setCreateError(errorMessageOf(err));
      setCreating(false);
    }
  }

  /**
   * Create Game, in drinking mode, asks the one question the mode cannot do
   * without: what a cup is worth. Everything else about the form has already
   * been answered, so this is a sheet on top of it rather than another screen —
   * cancelling puts the host back on a form still holding their name. §4.6, §51
   */
  function startCreate(identity: PlayerIdentityInput): void {
    if (!drinkingMode) {
      void create(identity, null);
      return;
    }
    setPendingIdentity(identity);
    setPriceError(null);
    setAskingPrice(true);
  }

  function confirmPrice(): void {
    const parsed = pointsPerDrinkSchema.safeParse(Number(priceDraft));
    if (!parsed.success) {
      setPriceError(parsed.error.issues[0]?.message ?? messageForErrorCode('VALIDATION_FAILED'));
      return;
    }
    const identity = pendingIdentity;
    if (!identity) return;
    setAskingPrice(false);
    void create(identity, parsed.data);
  }

  function toggleDrinkingMode(): void {
    setDrinkingMode((current) => !current);
    setDrinkingNonce((current) => current + 1);
    // The mode artwork takes the header, so whichever character was previewed
    // there is no longer what the box is showing. Picking again puts it back —
    // and this way the toggle always produces the motion it promised. §6.3
    setPreview(null);
  }

  const modeLogo = drinkingMode ? cinnaDrinkLogo : cinnaLogo;

  return (
    // Centred when it fits, scrolled when it does not: `justify-center` alone would
    // push the top of a tall create form above the viewport, where nothing can
    // reach it. The gameplay screen is the only one pinned to one viewport. §18.2
    <main className="scroll-area flex min-h-0 flex-1 flex-col p-safe">
      <div className="m-auto flex w-full max-w-sm flex-col gap-6">
        {/* A player bounced off a table arrives here with the reason still queued;
            App's toast host is what shows it to them. §35 */}
        <header className="flex flex-col items-center gap-1 text-center">
          {/*
            One box, two things in it, and the box is what has the height: the
            artwork inside it changes from a wide logo to a square sticker, and a
            container sized by its contents would jump by the difference at the
            moment of the swap — under the stretch, which is the one moment
            nothing else may move. §18.2

            It grows on the create screen into the room the title gives up, so the
            character a player is choosing is the biggest thing on the screen
            rather than a 48 px tile in a grid of 112. §6.3, §46
          */}
          <div
            className={[
              'mb-1 flex w-full items-end justify-center',
              'transition-[height] duration-300 ease-out-soft',
              /*
                Tall enough on the create screen that the chosen character is
                drawn at the size the Cinnamoroll is, which is what the box has
                to carry rather than the avatar: the logo is `h-full` and its
                width cap decides how much of that it actually draws, while the
                avatar is four fifths of the box with the last fifth reserved
                for the overshoot. Matching the two therefore means a taller
                box, not a taller avatar. §6.3, §46
              */
              mode === 'create' ? 'h-48 sm:h-52' : 'h-24 sm:h-28',
            ].join(' ')}
          >
            {preview ? (
              <Avatar
                // Remounted per pick — see AvatarPreview. §6.3
                key={preview.nonce}
                avatarKey={preview.avatarKey}
                /*
                  Four fifths of the box, sitting on its floor, and the last
                  fifth is what the overshoot stretches into. The stretch grows
                  upward from the bottom edge — filling the box would send the
                  tall frame of it out of the top, and on a phone where the form
                  is already taller than the screen that top is the edge of the
                  viewport: the character would lose its head at the exact moment
                  it is meant to be shown off. §18.2

                  The seat file, not the celebration variant: at this size the
                  large one buys a little sharpness on a 3x screen for five times
                  the bytes, per tap, on the screen where a player flicks through
                  112 characters. The tile they just tapped has the seat
                  file decoded already, so this draws in the same frame. §46
                */
                className="avatar-preview-stretch h-4/5 w-auto max-w-full"
              />
            ) : (
              /* Decorative: the h1 underneath already says the name, and a screen
                 reader hearing it twice is worse than not hearing the artwork. The
                 intrinsic size is declared so the heading does not jump down when
                 the image arrives — this is the first paint on the slowest
                 connection the app has. §19, §18.2 */
              <img
                /* Remounted whenever the mode is toggled, so the swap between the
                   two Cinnamorolls runs the same stretch a picked avatar does.
                   Keyed on the nonce rather than the src for the reason
                   AvatarPreview gives: re-rendering an element whose animation
                   has not changed does not start it again. §6.3 */
                key={`mode-${String(drinkingNonce)}`}
                src={modeLogo}
                alt=""
                aria-hidden="true"
                width={640}
                height={340}
                decoding="async"
                /*
                  `object-bottom`, because below about 356 px the width cap bites
                  before the height does and `contain` then centres the artwork
                  inside a box taller than it needs — leaving the logo floating
                  some 7 px above a floor the avatar that replaces it sits flush
                  on. That is a jump at the moment of the swap, which is the one
                  moment nothing but the stretch may move. §18.2

                  The stretch class is applied only once something has been
                  toggled: the very first paint of the app should be the logo
                  arriving, not a logo springing in from nowhere. §20.4
                */
                className={[
                  'h-full w-auto max-w-[70%] object-contain object-bottom',
                  drinkingNonce > 0 ? 'avatar-preview-stretch' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
            )}
          </div>
          {/*
            The title and the tagline fold away on the create screen and hand
            their room to the preview. Collapsed rather than unmounted, and left
            in the accessibility tree: it is still the name of the page, and this
            screen's only other heading is the h2 for the form on it. §19

            Animated by row size rather than height, the same way ShareCard's
            panel is, so it never needs a measured pixel value: the tagline
            already wraps to two lines at 320 px, and a `max-height` cap chosen
            to clear that wrap is a cap that a larger font — Android's text
            scaling scales the text and not the rem — eventually clips the
            tagline against, on the app's first screen, permanently. §18.2, §20.4
          */}
          <div
            className="grid w-full transition-[grid-template-rows,opacity] duration-300 ease-out-soft"
            style={{
              gridTemplateRows: mode === 'create' ? '0fr' : '1fr',
              opacity: mode === 'create' ? 0 : 1,
            }}
          >
            <div className="min-h-0 overflow-hidden">
              <h1 className="text-2xl font-semibold text-ink">Point Counter</h1>
              <p className="text-ink-muted">
                Minimizer score counter.{' '}
                {/* The hyphen is a break opportunity, and a 320 px phone takes
                    it: "Cinna-" / "enabled" across two lines. It breaks before
                    the word instead. §18.2 */}
                <em className="whitespace-nowrap">Cinna-enabled</em>
              </p>
            </div>
          </div>
        </header>

        {mode === 'create' ? (
          <section aria-label="Create a game" className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <IconButton
                label="Back"
                onClick={() => {
                  setMode('choose');
                  setCreateError(null);
                  // The form unmounts with it and comes back on the default
                  // avatar, so the header has to come back on the logo — a
                  // preview left standing would be showing a choice the form
                  // underneath no longer holds. §6.3
                  setPreview(null);
                }}
              >
                ←
              </IconButton>
              <h2 className="text-lg font-semibold text-ink">New table</h2>

              {/*
                Drinking mode, on the same line as the title and pushed to the
                far end of it. It belongs to the table being created rather than
                to the person creating it, which is why it sits with the heading
                and not among the name and avatar fields below. §4.6

                A real switch: `role="switch"` with `aria-checked`, so it is
                announced as on or off rather than as a button that might have
                done something. §19
              */}
              <button
                type="button"
                role="switch"
                aria-checked={drinkingMode}
                disabled={creating}
                onClick={toggleDrinkingMode}
                className={[
                  'ml-auto flex min-h-tap shrink-0 items-center gap-2 rounded-control border px-2.5',
                  'transition-[background-color,border-color] duration-200 ease-out-soft',
                  'disabled:pointer-events-none disabled:opacity-45',
                  drinkingMode
                    ? 'border-negative/60 bg-negative-soft text-negative'
                    : 'border-line bg-surface text-ink-muted',
                ].join(' ')}
              >
                <span className="text-xs font-semibold tracking-wide uppercase">Drinking</span>
                {/* The track and its knob. Motion only — the label above and the
                    switch role are what carry the state. §19 */}
                <span
                  aria-hidden="true"
                  className={[
                    'relative block h-5 w-9 shrink-0 rounded-full transition-colors duration-200 ease-out-soft',
                    drinkingMode ? 'bg-negative/70' : 'bg-line',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'absolute top-0.5 left-0.5 block size-4 rounded-full bg-surface',
                      'transition-transform duration-200 ease-out-soft',
                      drinkingMode ? 'translate-x-4' : 'translate-x-0',
                    ].join(' ')}
                  />
                </span>
              </button>
            </div>
            <JoinForm
              submitLabel="Create Game"
              submitting={creating}
              error={createError}
              onAvatarChange={(avatarKey) => {
                setPreview((current) => ({ avatarKey, nonce: (current?.nonce ?? 0) + 1 }));
              }}
              onSubmit={startCreate}
            />
          </section>
        ) : (
          <div className="flex flex-col gap-6">
            <Button
              size="lg"
              fullWidth
              onClick={() => {
                setMode('create');
              }}
            >
              Create Game
            </Button>

            <div className="flex items-center gap-3" aria-hidden="true">
              <span className="h-px flex-1 bg-line" />
              <span className="text-xs tracking-widest text-ink-faint uppercase">or</span>
              <span className="h-px flex-1 bg-line" />
            </div>

            <form
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                submitCode();
              }}
              className="flex flex-col gap-3"
            >
              {/*
                "Got a code?" and the way in for somebody who has never played,
                on one line. §6.2

                The tutorial belongs *above* the code field and beside that
                label rather than below the Join button, because the two are
                answers to the same question — a player looking at this screen
                either knows what to do with a code or does not — and a control
                under the fold on the app's first screen is a control nobody
                finds. The same arrangement, and the same reasoning, as the
                drinking switch beside the "New table" heading. §4.6

                It carries no state of its own: the picker is a sheet, and which
                language was chosen lives in the URL it navigates to.
              */}
              <div className="flex items-center gap-2">
                <label htmlFor="join-code" className="text-lg font-semibold text-ink">
                  Got a code?
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setPickingLanguage(true);
                  }}
                  className={[
                    'ml-auto flex min-h-tap shrink-0 items-center gap-1.5 rounded-control',
                    'border border-line bg-surface px-3 text-sm font-semibold text-ink-muted',
                    'transition-[transform,border-color] duration-150 ease-out-soft',
                    'active:scale-[0.97] hover:border-ink-faint hover:text-ink',
                  ].join(' ')}
                >
                  <span aria-hidden="true">🎓</span>
                  Tutorial
                </button>
              </div>
              <input
                id="join-code"
                value={code}
                onChange={(event) => {
                  setCode(displayCode(event.target.value));
                  setCodeError(null);
                }}
                type="text"
                // Text, not numeric: codes mix letters and digits, and a numeric
                // keypad would hide half the alphabet. §6.2
                inputMode="text"
                autoCapitalize="characters"
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
                enterKeyHint="go"
                // Long enough to paste a whole join URL into. §6.2
                maxLength={128}
                placeholder="CAT-284"
                aria-invalid={codeError !== null}
                aria-describedby={codeError !== null ? 'join-code-error' : undefined}
                data-numeric
                className="min-h-tap w-full rounded-control border border-line bg-raised px-4 text-center text-xl tracking-[0.2em] text-ink uppercase placeholder:text-ink-faint"
              />
              {codeError !== null ? (
                <p id="join-code-error" role="alert" className="text-sm text-negative">
                  {codeError}
                </p>
              ) : null}
              <Button type="submit" variant="secondary" size="lg" fullWidth>
                Join Table
              </Button>
            </form>

            {/*
              Which release this phone is running. §44

              It is here and nowhere else: the landing screen is the one surface
              every device passes through and the only one with room to spare, and
              a version is something somebody reads out once when a table is
              behaving strangely. On the score screen it would be a line competing
              with the scores. §14.1

              Not `aria-hidden`. It is small and muted because it is the least
              important thing on the page, but a player reporting a problem may be
              the one using a screen reader, and hiding it would leave them the
              only person who cannot say which build they are on. §19
            */}
            <p className="text-right text-[0.6875rem] leading-none text-ink-faint">
              <span className="sr-only">App version </span>
              {__APP_VERSION__}
            </p>
          </div>
        )}
      </div>

      {/*
        §4.6 — the only number drinking mode needs, asked once. It is the
        exchange rate the whole night is priced in, so it cannot be changed
        later, and the table is told what it bought before it is created.
      */}
      <Sheet
        open={askingPrice}
        onClose={() => {
          setAskingPrice(false);
        }}
        title="How many points for a cup?"
        description="Finishing a drink adds this to your score. It cannot be changed later."
        footer={
          <div className="grid grid-cols-[1fr_1.2fr] gap-2">
            <button
              type="button"
              onClick={() => {
                setAskingPrice(false);
              }}
              className="min-h-tap rounded-control border border-line bg-surface font-semibold text-ink"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={creating}
              onClick={confirmPrice}
              className="min-h-tap rounded-control border border-negative/50 bg-negative-soft font-semibold text-negative disabled:opacity-45"
            >
              Create Game
            </button>
          </div>
        }
      >
        <label htmlFor="points-per-drink" className="sr-only">
          Points per cup
        </label>
        <input
          id="points-per-drink"
          data-autofocus
          value={priceDraft}
          onChange={(event) => {
            // Digits only, so the field can never hold something the schema
            // would have to reject with a message about numbers. §10.3
            setPriceDraft(event.target.value.replace(/[^0-9]/g, ''));
            setPriceError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              confirmPrice();
            }
          }}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={String(MAX_POINTS_PER_DRINK).length}
          aria-invalid={priceError !== null}
          aria-describedby={priceError !== null ? 'points-per-drink-error' : undefined}
          data-numeric
          className="min-h-tap w-full rounded-control border border-line bg-raised px-4 text-center text-2xl font-bold text-ink"
        />
        {priceError !== null ? (
          <p id="points-per-drink-error" role="alert" className="mt-2 text-sm text-negative">
            {priceError}
          </p>
        ) : null}
      </Sheet>

      {/*
        The language orbs. Imported directly rather than lazily, and that is
        deliberate: it is four names, four inline SVG flags and a sheet the app
        already ships. The *tutorial* is what is deferred, behind the route this
        navigates to — see TutorialRoute. §17.1
      */}
      <LanguagePicker
        open={pickingLanguage}
        onClose={() => {
          setPickingLanguage(false);
        }}
        onPick={(locale) => {
          setPickingLanguage(false);
          navigate(paths.tutorial(locale));
        }}
      />
    </main>
  );
}
